import React, { useMemo, useRef, type FC } from 'react'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import {
  Flex,
  FormElement,
  SELECT_VARIANT_WITH_CHECKBOX,
  Select,
  Text,
  Toggle,
} from 'mailup-web-components'

import useActivityStream from 'Hooks/useActivityStream'

import { activityFiltersToEventTypes } from 'Pages/RecipientsList/recoil/services/recipient'

import type { ActivityStreamFilterType } from 'Pages/RecipientsList/model'
import {
  changeActivityStreamFiltersCallback,
  toggleActivityStreamAllFiltersCallback,
} from 'Pages/RecipientsList/recoil/callbacks/recipient'
import { recipientActivityStreamFiltersState } from 'Pages/RecipientsList/recoil/state/recipient'

interface FiltersProps {
  idRecipient: number
  loading: boolean
  channel: 'email' | 'sms'
}

const Filters: FC<FiltersProps> = ({
  idRecipient,
  channel,

  loading,
}) => {
  const intl = useIntl()

  const firstRender = useRef(true)
  const expanded = useRef(false)

  const { fetchResults } = useActivityStream(idRecipient, channel)

  const { allFilters, filters } = useRecoilValue(
    recipientActivityStreamFiltersState,
  )

  const toggle = useRecoilCallback(toggleActivityStreamAllFiltersCallback, [])

  const changeFilters = useRecoilCallback(
    changeActivityStreamFiltersCallback,
    [],
  )

  const handleFiltersChange = (values: ActivityStreamFilterType[]): void => {
    void changeFilters(values)

    if (!expanded.current) {
      void fetchResults(
        1,
        10,
        activityFiltersToEventTypes(allFilters, values, channel),
      )
    }
  }

  const handleExpandCollapse = (_expanded: boolean): void => {
    expanded.current = _expanded

    if (firstRender.current) {
      firstRender.current = false
      return
    }

    if (!_expanded) {
      void fetchResults(
        1,
        10,
        activityFiltersToEventTypes(allFilters, filters, channel),
      )
    }
  }

  const handleToggle = ({
    target: { checked },
  }: React.ChangeEvent<HTMLInputElement>): void => {
    void toggle(channel)

    void fetchResults(
      1,
      10,
      activityFiltersToEventTypes(checked, filters, channel),
    )
  }

  const options = useMemo(() => {
    //    'SubscriptionRequest', 'SubscriptionConfirmation', '', '', '', '', '', ''

    return channel === 'email'
      ? [
          {
            label: intl.formatMessage({
              id: 'Load.ActiveRecipientClickDetailsEmailActivityStream.Subscription',
            }),
            value: 'subscription',
          },
          {
            label: intl.formatMessage({
              id: 'Load.ActiveRecipientClickDetailsEmailActivityStream.Unsubscription',
            }),
            value: 'unsubscription',
          },
          {
            label: intl.formatMessage({
              id: 'Load.ActiveRecipientClickDetailsEmailActivityStream.Bounces',
            }),
            value: 'bounces',
          },
          {
            label: intl.formatMessage({
              id: 'Load.ActiveRecipientClickDetailsEmailActivityStream.Clicks',
            }),
            value: 'clicks',
          },
          {
            label: intl.formatMessage({
              id: 'Load.ActiveRecipientClickDetailsEmailActivityStream.Sendings',
            }),
            value: 'sendings',
          },
          {
            label: intl.formatMessage({
              id: 'Load.ActiveRecipientClickDetailsEmailActivityStream.Opens',
            }),
            value: 'opens',
          },
        ]
      : [
          {
            label: intl.formatMessage({
              id: 'Load.ActiveRecipientClickDetailsEmailActivityStream.Subscription',
            }),
            value: 'subscription',
          },
          {
            label: intl.formatMessage({
              id: 'Load.ActiveRecipientClickDetailsEmailActivityStream.Unsubscription',
            }),
            value: 'unsubscription',
          },
          {
            label: intl.formatMessage({
              id: 'Load.ActiveRecipientClickDetailsEmailActivityStream.Clicks',
            }),
            value: 'clicks',
          },
          {
            label: intl.formatMessage({
              id: 'Load.ActiveRecipientClickDetailsEmailActivityStream.Sendings',
            }),

            value: 'sendings',
          },
        ]
  }, [channel])

  const RightComponent: FC = () => {
    return (
      <Flex columnGap="8px">
        <Toggle
          variant="sm"
          disabled={loading}
          checked={allFilters}
          onChange={handleToggle}
          name="select-all"
          value="true"
        />

        <Text data-mu-test="activity-stream__all-filters">
          {intl.formatMessage({
            id: 'Load.RecipientsList.ActivityStreamFiltersSelectAll',
          })}
        </Text>
      </Flex>
    )
  }

  return (
    <FormElement
      label={intl.formatMessage({
        id: 'Load.RecipientsList.ActivityStreamFiltersLabel',
      })}
      LabelRightComponent={RightComponent}
    >
      <Select
        variant={SELECT_VARIANT_WITH_CHECKBOX}
        multiple
        searchable={false}
        disabled={loading || allFilters}
        loading={loading}
        options={options}
        value={filters}
        onChange={handleFiltersChange}
        data-mu-test="activity-stream__filters"
        onExpandCollapse={handleExpandCollapse}
        placeholder={intl.formatMessage({
          id: 'Load.RecipientsList.SelectStatusPlaceholder',
        })}
      />
    </FormElement>
  )
}

export default Filters
